import { useMemo } from "react";
import { Command } from "cmdk";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useCommandPalette } from "@/lib/paletteStore";
import { queryClient } from "@/lib/query-client";
import { client } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { isAssistantWorkspace, type Worktree } from "@rivets/core";
import { NavigationIcon, type NavigationIconName } from "@/components/NavigationIcon";
import { shortcutChordLabel, useKeyboardShortcutSettings, useShortcutCommand } from "@/lib/keyboardShortcuts";
import { useNavigationHistory } from "@/lib/navigationHistory";
import { useAssistantDrawer } from "@/features/assistant/assistantStore";
import { useSidebarLayout } from "@/lib/sidebarLayout";

const NAV_ITEMS: Array<{ value: string; label: string; icon: NavigationIconName; path: (orgId: string) => string }> = [
  { value: "tasks", label: "Tasks", icon: "home", path: (orgId) => `/o/${orgId}` },
  { value: "settings", label: "Settings", icon: "settings", path: (orgId) => `/o/${orgId}/settings` },
];

export function CommandPalette({ currentOrgId }: { currentOrgId?: string }) {
  const open = useCommandPalette((s) => s.open);
  const setOpen = useCommandPalette((s) => s.setOpen);
  const contextCommands = useCommandPalette((s) => s.contextCommands);
  const navigate = useNavigate();
  const router = useRouter();
  const { canGoBack, canGoForward } = useNavigationHistory();
  const bindings = useKeyboardShortcutSettings((state) => state.bindings);
  const setAssistantOpen = useAssistantDrawer((s) => s.setOpen);
  const assistantOpen = useAssistantDrawer((s) => s.open);
  const { collapsed, toggle: toggleSidebar } = useSidebarLayout();

  useShortcutCommand("commandPalette", () => setOpen(true));

  const { data: me } = useQuery({
    queryKey: qk.me(),
    queryFn: () => client.me(),
    enabled: open,
    staleTime: 60_000,
  });

  // Pull worktrees from whatever's already cached (populated by the overview
  // page) rather than firing new requests every time the palette opens.
  const cachedWorktrees = useMemo(() => {
    if (!open) return [];
    const entries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["worktrees"] });
    const all: Worktree[] = [];
    for (const entry of entries) {
      const data = entry.state.data as { worktrees: Worktree[] } | undefined;
      // Assistant scratch workspaces are private per-member rows, not tasks:
      // they must never be offered as a place to navigate to (FRNT-699).
      if (data?.worktrees) {
        all.push(...data.worktrees.filter(
          (worktree) => worktree.status !== "archived" && !isAssistantWorkspace(worktree.source),
        ));
      }
    }
    return all.filter((w) => !currentOrgId || w.orgId === currentOrgId);
  }, [open, currentOrgId]);

  function go(path: string) {
    setOpen(false);
    navigate({ to: path });
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      className="palette"
    >
      <Command.Input placeholder="Jump to… (orgs, tasks, actions)" className="palette-input" />
      <Command.List className="palette-list">
        <Command.Empty className="palette-empty">No matches.</Command.Empty>

        {contextCommands.length > 0 && (
          <Command.Group heading="Actions">
            {contextCommands.map((cmd) => (
              <Command.Item
                key={cmd.id}
                value={`${cmd.label} ${cmd.keywords ?? ""}`}
                onSelect={() => {
                  setOpen(false);
                  cmd.perform();
                }}
              >
                {cmd.label}
                {cmd.hint && <span className="palette-hint">{cmd.hint}</span>}
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {cachedWorktrees.length > 0 && (
          <Command.Group heading="Recent tasks">
            {cachedWorktrees.map((wt) => (
              <Command.Item
                key={wt.id}
                value={`worktree ${wt.name} ${wt.branch}`}
                onSelect={() => go(`/o/${wt.orgId}/wt/${wt.id}`)}
              >
                {wt.name}
                {wt.status === "awaiting_approval" ? <span className="palette-hint">Needs approval</span> : null}
                <span className="palette-hint mono">{wt.branch}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {currentOrgId && (
          <Command.Group heading="Assistant">
            <Command.Item
              value="assistant rivets show hide toggle ask"
              onSelect={() => {
                setOpen(false);
                setAssistantOpen(!assistantOpen);
              }}
            >
              <span className="palette-item-main">{assistantOpen ? "Hide Rivets assistant" : "Show Rivets assistant"}</span>
              <span className="palette-hint">{shortcutChordLabel(bindings.toggleAssistant)}</span>
            </Command.Item>
          </Command.Group>
        )}

        {currentOrgId && (
          <Command.Group heading="View">
            <Command.Item
              value="toggle navigation sidebar collapse expand"
              onSelect={() => {
                setOpen(false);
                toggleSidebar();
              }}
            >
              <span className="palette-item-main">{collapsed ? "Expand navigation" : "Collapse navigation"}</span>
              <span className="palette-hint">{shortcutChordLabel(bindings.toggleSidebar)}</span>
            </Command.Item>
            {/* Listed only when they would do something: a palette row that
                silently no-ops is worse than one that is not there. */}
            {canGoBack && (
              <Command.Item
                value="back history previous page"
                onSelect={() => {
                  setOpen(false);
                  router.history.back();
                }}
              >
                <span className="palette-item-main">Back</span>
                <span className="palette-hint">{shortcutChordLabel(bindings.goBack)}</span>
              </Command.Item>
            )}
            {canGoForward && (
              <Command.Item
                value="forward history next page"
                onSelect={() => {
                  setOpen(false);
                  router.history.forward();
                }}
              >
                <span className="palette-item-main">Forward</span>
                <span className="palette-hint">{shortcutChordLabel(bindings.goForward)}</span>
              </Command.Item>
            )}
          </Command.Group>
        )}

        {currentOrgId && (
          <Command.Group heading="Navigate">
            {NAV_ITEMS.map((item) => (
              <Command.Item key={item.value} value={item.value} onSelect={() => go(item.path(currentOrgId))}>
                <span className="palette-item-main"><NavigationIcon name={item.icon} />{item.label}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {me && me.orgs.length > 1 && (
          <Command.Group heading="Switch org">
            {me.orgs
              .filter((o) => o.id !== currentOrgId)
              .map((org) => (
                <Command.Item key={org.id} value={`org ${org.name}`} onSelect={() => go(`/o/${org.id}`)}>
                  {org.name}
                </Command.Item>
              ))}
          </Command.Group>
        )}
      </Command.List>
      <div className="palette-footer" aria-hidden="true">
        <span>↑↓ navigate</span>
        <span>↵ open</span>
        <span>esc close</span>
      </div>
    </Command.Dialog>
  );
}
